import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Syringe, Download, Save, Eye, Trash2, Plus, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TODAY = new Date().toISOString().slice(0, 10);

const EMPTY = {
  date_vaccinated: TODAY,
  owner_last_name: '', owner_first_name: '', owner_address: '', owner_box: '', owner_phone: '',
  pet_name: '', species: 'Dog', pet_sex_status: 'Female Spayed',
  age: '', weight: '', breed: '', colour: '',
  microchip_number: '', microchip_location: 'Dorsal Shoulders',
  vaccine_name: 'Boehringer Ingelheim',
  vaccine_producer: 'Imrab 3TF',
  serial_number: '', lot_expiration: '',
  vaccination_expires: '', interval_years: 1,
  notes: '',
  vet_name: 'Dr. Samantha Dorman, MSc, BVSc',
  vet_title: 'Veterinarian and Veterinary Travel Consultant',
  license_number: '139',
  accreditation_number: '',
};

// Auto-compute next-due date from vaccination date + interval
function addYears(iso, yrs) {
  if (!iso) return '';
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + Number(yrs || 1));
  return d.toISOString().slice(0, 10);
}

export default function RabiesVaccinations() {
  const password = sessionStorage.getItem('admin_password') || '';
  const auth = { 'Authorization': `Basic ${btoa(':' + password)}` };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);

  const fetchList = async () => {
    const r = await fetch(`${API_URL}/api/admin/rabies-vaccinations`, { headers: auth });
    setList(await r.json());
  };
  useEffect(() => { fetchList(); }, []);

  const setField = (k, v) => setForm(f => {
    const next = { ...f, [k]: v };
    if (k === 'date_vaccinated' || k === 'interval_years') {
      next.vaccination_expires = addYears(next.date_vaccinated, next.interval_years);
    }
    return next;
  });

  const preview = async () => {
    const r = await fetch(`${API_URL}/api/admin/rabies-vaccinations/preview-pdf`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify(form),
    });
    window.open(URL.createObjectURL(await r.blob()), '_blank');
  };

  const save = async () => {
    const isEdit = Boolean(form.id);
    const r = await fetch(`${API_URL}/api/admin/rabies-vaccinations${isEdit ? '/' + form.id : ''}`, {
      method: isEdit ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json', ...auth },
      body: JSON.stringify(form),
    });
    if (!r.ok) return alert(`Save failed: HTTP ${r.status}`);
    setForm(null); fetchList();
  };

  const download = async (it) => {
    const r = await fetch(`${API_URL}/api/admin/rabies-vaccinations/${it.id}/pdf`, { headers: auth });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(await r.blob());
    a.download = `rabies-vaccination-${it.certificate_number}.pdf`;
    a.click();
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete certificate ${it.certificate_number}?`)) return;
    await fetch(`${API_URL}/api/admin/rabies-vaccinations/${it.id}`, { method: 'DELETE', headers: auth });
    fetchList();
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-gray-500 hover:text-gray-700"><ArrowLeft className="w-5 h-5" /></Link>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <Syringe className="w-5 h-5 text-rose-600" /> Rabies Vaccination Certificates
              </h1>
              <p className="text-xs text-gray-500">Issue signed rabies vaccination certificates on Pawsome Ventures letterhead.</p>
            </div>
          </div>
          {!form && (
            <Button onClick={() => setForm({ ...EMPTY })} data-testid="new-rabies-cert-btn">
              <Plus className="w-4 h-4 mr-2" /> New Certificate
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {form ? (
          <FormView form={form} setField={setField} onPreview={preview} onSave={save} onCancel={() => setForm(null)} />
        ) : (
          <ListView list={list} onEdit={setForm} onDownload={download} onDelete={remove} />
        )}
      </div>
    </div>
  );
}


function ListView({ list, onEdit, onDownload, onDelete }) {
  if (!list.length) return (
    <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
      No rabies certificates yet. Click <b>New Certificate</b> to issue your first one.
    </div>
  );
  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b">
          <tr className="text-left text-xs uppercase text-gray-500">
            <th className="px-4 py-3">Cert #</th>
            <th className="px-4 py-3">Pet</th>
            <th className="px-4 py-3">Owner</th>
            <th className="px-4 py-3">Vaccinated</th>
            <th className="px-4 py-3">Expires</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.map(it => (
            <tr key={it.id} className="border-t hover:bg-gray-50">
              <td className="px-4 py-3 font-mono text-rose-700 font-semibold">{it.certificate_number}</td>
              <td className="px-4 py-3"><div className="font-medium">{it.pet_name}</div><div className="text-xs text-gray-500">{it.species} · {it.breed}</div></td>
              <td className="px-4 py-3">{it.owner_first_name} {it.owner_last_name}</td>
              <td className="px-4 py-3 text-xs">{it.date_vaccinated}</td>
              <td className="px-4 py-3 text-xs">{it.vaccination_expires}</td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button onClick={() => onDownload(it)} className="p-2 text-rose-700 hover:bg-rose-50 rounded" title="Download"><Download className="w-4 h-4" /></button>
                <button onClick={() => onEdit({ ...it })} className="p-2 text-blue-600 hover:bg-blue-50 rounded" title="Edit"><Syringe className="w-4 h-4" /></button>
                <button onClick={() => onDelete(it)} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function F({ form, setField, label, k, type = 'text', options, ph }) {
  if (options) {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-600">{label}</label>
        <select className="border rounded w-full px-3 py-2 mt-1" value={form[k] ?? ''} onChange={e => setField(k, e.target.value)}>
          {options.map(o => <option key={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (type === 'textarea') {
    return (
      <div>
        <label className="text-xs font-semibold text-gray-600">{label}</label>
        <textarea className="border rounded w-full px-3 py-2 mt-1" rows="3" value={form[k] ?? ''} onChange={e => setField(k, e.target.value)} />
      </div>
    );
  }
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600">{label}</label>
      <input type={type} className="border rounded w-full px-3 py-2 mt-1" placeholder={ph} value={form[k] ?? ''} onChange={e => setField(k, type === 'number' ? Number(e.target.value) : e.target.value)} />
    </div>
  );
}

function CB({ form, setField, label, k }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={Boolean(form[k])} onChange={e => setField(k, e.target.checked)} /> {label}
    </label>
  );
}

function FormView({ form, setField, onPreview, onSave, onCancel }) {
  return (
    <div className="bg-white rounded-lg border p-6 space-y-6">
      <h2 className="text-lg font-semibold">{form.id ? `Edit ${form.certificate_number}` : 'New Rabies Certificate'}</h2>

      <section>
        <h3 className="text-sm font-semibold text-rose-700 mb-3">Certificate</h3>
        <div className="grid grid-cols-2 gap-3">
          <F form={form} setField={setField} label="Certificate Number" k="certificate_number" ph="Leave blank to auto-assign RV-YYYY-NNNN" />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-rose-700 mb-3">Owner</h3>
        <div className="grid grid-cols-2 gap-3">
          <F form={form} setField={setField} label="First Name" k="owner_first_name" />
          <F form={form} setField={setField} label="Last Name" k="owner_last_name" />
          <div className="col-span-2"><F form={form} setField={setField} label="Address" k="owner_address" ph="560 Marina Dr., Prospect, GT, GCM" /></div>
          <F form={form} setField={setField} label="P.O. Box" k="owner_box" />
          <F form={form} setField={setField} label="Telephone" k="owner_phone" />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-rose-700 mb-3">Pet</h3>
        <div className="grid grid-cols-3 gap-3">
          <F form={form} setField={setField} label="Pet Name" k="pet_name" />
          <F form={form} setField={setField} label="Species" k="species" options={['Dog', 'Cat', 'Other']} />
          <F form={form} setField={setField} label="Sex & Status" k="pet_sex_status" options={['Female Spayed', 'Female Intact', 'Male Neutered', 'Male Intact', 'Unknown']} />
          <F form={form} setField={setField} label="Breed" k="breed" />
          <F form={form} setField={setField} label="Age" k="age" ph="5 Yrs" />
          <F form={form} setField={setField} label="Weight / Size" k="weight" ph="54.2 lbs" />
          <F form={form} setField={setField} label="Colour" k="colour" />
          <F form={form} setField={setField} label="Microchip Number" k="microchip_number" />
          <F form={form} setField={setField} label="Microchip Location" k="microchip_location" />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-rose-700 mb-3">Vaccination</h3>
        <div className="grid grid-cols-2 gap-3">
          <F form={form} setField={setField} label="Date Vaccinated" k="date_vaccinated" type="date" />
          <F form={form} setField={setField} label="Interval (years)" k="interval_years" options={[1, 3]} />
          <F form={form} setField={setField} label="Vaccine Name" k="vaccine_name" />
          <F form={form} setField={setField} label="Producer" k="vaccine_producer" />
          <F form={form} setField={setField} label="Serial / Lot #" k="serial_number" />
          <F form={form} setField={setField} label="Lot Expiration" k="lot_expiration" type="date" />
          <F form={form} setField={setField} label="Vaccination Expires (auto-calculated)" k="vaccination_expires" type="date" />
        </div>
        <div className="mt-3"><F form={form} setField={setField} label="Notes (optional)" k="notes" type="textarea" /></div>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-rose-700 mb-3">Signed By</h3>
        <div className="grid grid-cols-2 gap-3">
          <F form={form} setField={setField} label="Signer Name" k="vet_name" />
          <F form={form} setField={setField} label="Title" k="vet_title" />
          <F form={form} setField={setField} label="License Number" k="license_number" />
          <F form={form} setField={setField} label="Accreditation Number (optional)" k="accreditation_number" ph="Leave blank until received" />
        </div>
        <p className="text-xs text-gray-500 mt-2">A DocuSign-style e-signature block is applied automatically at PDF generation time — a cursive rendering of the signer name plus a timestamp and document-hash audit trail.</p>
      </section>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}><X className="w-4 h-4 mr-2" /> Cancel</Button>
        <Button variant="outline" onClick={onPreview}><Eye className="w-4 h-4 mr-2" /> Preview PDF</Button>
        <Button onClick={onSave}><Save className="w-4 h-4 mr-2" /> Save Certificate</Button>
      </div>
    </div>
  );
}
